import "./postsurveypage.css";
import { Button, Container, Form, ProgressBar, Modal, Table } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useApi } from "../../contexts/ApiProvider";
import { useUser } from "../../contexts/UserProvider";
import { useNavigate } from "react-router-dom";
import { userActions } from "../../actions/UserActions";

export function PostSurveyQuestionnaire({ context }) {
  const { user } = useUser();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const api = useApi();

  // Retrieve from Redux store (adjust as needed for your app)
  const store_form = useSelector((state) => state.user.postsurvey_form.form);
  const submitted = useSelector((state) => state.user.postsurvey_form.submitted);

  // Local state for form data
  const [formData, setFormData] = useState(store_form || {});
  const [errors, setErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [missingFields, setMissingFields] = useState([]);

  useEffect(() => {
    setFormData(store_form);
  }, [store_form]);

  // Handle standard text/radio/textarea changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value.trim() === "" ? null : value,
    });
  };

  // Helper for handling scale-based radio selections
  // If you want to allow unchecking an already checked radio, you can add logic here.
  // Currently, it will just select the clicked option (standard radio behavior).
  const handleLikertChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

// Q1_overlap_impact
// Q2a_overlap_more_sources
// Q2b_overlap_helped_narrow
// Q3a_overlap_uncertainty_direct_same_ward
// Q3b_overlap_uncertainty_direct_diff_ward_same_spec
// Q3c_overlap_uncertainty_direct_diff_ward_diff_spec
// Q3d_overlap_uncertainty_environment
// Q3e_overlap_uncertainty_hc_team
// Q3f_overlap_uncertainty_other
// Q4_overlap_comment
// Q5_genetic_impact
// Q6a_genetic_more_sources
// Q6b_genetic_helped_narrow
// Q7a_genetic_uncertainty_direct_same_ward
// Q7b_genetic_uncertainty_direct_diff_ward_same_spec
// Q7c_genetic_uncertainty_direct_diff_ward_diff_spec
// Q7d_genetic_uncertainty_environment
// Q7e_genetic_uncertainty_hc_team
// Q7f_genetic_uncertainty_other
// Q8_genetic_comment
// Q9_dashboard_info_comment
// Q10a_dashboard_overall_experience_rating
// Q10b_dashboard_overall_experience_comment
// Q11a_dashboard_user_friendliness_rating
// Q11b_dashboard_user_friendliness_comment
// Q12a_dashboard_clarity_case_list
// Q12b_dashboard_clarity_overlap
// Q12c_dashboard_clarity_genetic
// Q12d_dashboard_clarity_comment
// Q13_dashboard_challenges
// Q13_dashboard_challenges_comment
// Q14_dashboard_recommendations
// Q15_dashboard_additional_feedback

  // Check for missing required fields
  const checkForMissingFields = () => {
    // Adjust required fields as appropriate
    const requiredFields = [
      // Example required fields for each question
      { name: "Q1_overlap_impact", question: "1. Overlap information - Overall impact" },
      { name: "Q2a_overlap_more_sources", question: "2a. Overlap information - Helped identify more potential sources" },
      { name: "Q2b_overlap_helped_narrow", question: "2b. Overlap information - Helped narrow down potential sources" },
      { name: "Q3a_overlap_uncertainty_direct_same_ward", question: "3a. Overlap information - Impact on uncertainty" },
      { name: "Q3b_overlap_uncertainty_direct_diff_ward_same_spec", question: "3b. Overlap information - Impact on uncertainty" },
      { name: "Q3c_overlap_uncertainty_direct_diff_ward_diff_spec", question: "3c. Overlap information - Impact on uncertainty" },
      { name: "Q3d_overlap_uncertainty_environment", question: "3d. Overlap information - Impact on uncertainty" },
      { name: "Q3e_overlap_uncertainty_hc_team", question: "3e. Overlap information - Impact on uncertainty" },
      { name: "Q3f_overlap_uncertainty_other", question: "3f. Overlap information - Impact on uncertainty" },
      { name: "Q4_overlap_comment", question: "4. Overlap information - Additional comments" },
      { name: "Q5_genetic_impact", question: "5. Genetic information - Overall impact" },
      { name: "Q6a_genetic_more_sources", question: "6a. Genetic information - Helped identify more potential sources" },
      { name: "Q6b_genetic_helped_narrow", question: "6b. Genetic information - Helped narrow down potential sources" },
      { name: "Q7a_genetic_uncertainty_direct_same_ward", question: "7a. Genetic information - Impact on uncertainty" },
      { name: "Q7b_genetic_uncertainty_direct_diff_ward_same_spec", question: "7b. Genetic information - Impact on uncertainty" },
      { name: "Q7c_genetic_uncertainty_direct_diff_ward_diff_spec", question: "7c. Genetic information - Impact on uncertainty" },
      { name: "Q7d_genetic_uncertainty_environment", question: "7d. Genetic information - Impact on uncertainty" },
      { name: "Q7e_genetic_uncertainty_hc_team", question: "7e. Genetic information - Impact on uncertainty" },
      { name: "Q7f_genetic_uncertainty_other", question: "7f. Genetic information - Impact on uncertainty" },
      { name: "Q8_genetic_comment", question: "8. Genetic information - Additional comments" },
      { name: "Q9_dashboard_info_comment", question: "9. Dashboard - Specific features influencing decisions" },
      { name: "Q10a_dashboard_overall_experience_rating", question: "10a. Dashboard - Overall Experience" },
      { name: "Q10b_dashboard_overall_experience_comment", question: "10b. Dashboard - Overall Experience - Additional comments" },
      { name: "Q11a_dashboard_user_friendliness_rating", question: "11a. Dashboard - User-Friendliness" },
      { name: "Q11b_dashboard_user_friendliness_comment", question: "11b. Dashboard - User-Friendliness - Additional comments" },
      { name: "Q12a_dashboard_clarity_case_list", question: "11a. Dashboard - Presentation & Clarity - Case List" },
      { name: "Q12b_dashboard_clarity_overlap", question: "11b. Dashboard - Presentation & Clarity - Overlap" },
      { name: "Q12c_dashboard_clarity_genetic", question: "11c. Dashboard - Presentation & Clarity - Genetic" },
      { name: "Q12d_dashboard_clarity_comment", question: "11d. Dashboard - Presentation & Clarity - Additional comments" },
      { name: "Q13_dashboard_challenges", question: "13. Dashboard - Challenges or Difficulties" },
      { name: "Q13_dashboard_challenges_comment", question: "13. Dashboard - Challenges or Difficulties - Additional comments" },
      { name: "Q14_dashboard_recommendations", question: "14. Dashboard - Recommendations for improvement" },
      { name: "Q15_dashboard_additional_feedback", question: "15. Dashboard - Other feedback" },
    ];

    const missing = requiredFields
      .filter((field) => !formData[field.name])
      .map((field) => field.question);

    setMissingFields(missing);
    return missing.length > 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (checkForMissingFields()) {
      setShowConfirmation(true);
      return;
    }
    submitForm();
  };

  const handleNavigate = () => {
    // Navigate after submission
    navigate("/");
  };

  const submitForm = async () => {
    const updatedForm = {
      ...formData,
      id: Number(user.id),
      submitted: true,
    };

    // Update Redux store
    dispatch(userActions.updatePostSurveySelection(updatedForm));
    dispatch(userActions.submitPostSurvey());

    // Submit form data to backend if not already submitted
    if (!submitted) {
      await api.post("/me/postsurvey", updatedForm);
    }

    handleNavigate();
  };

  // --------------------------
  // Scale arrays for the tables
  // --------------------------
  const helpfulScale = [
    "Very helpful",
    "Somewhat helpful",
    "Neutral",
    "Somewhat unhelpful",
    "Very unhelpful",
    "Not sure",
  ];

  const agreementScale = [
    "Strongly agree",
    "Agree",
    "Neutral",
    "Disagree",
    "Strongly disagree",
    "Not sure",
  ];

  const uncertaintyScale = [
    "Strongly decreased",
    "Decreased",
    "No change",
    "Increased",
    "Strongly increased",
    "Not sure",
  ];

  const clarityScale = [
    "Very Clear",
    "Moderately Clear",
    "Neutral",
    "Somewhat Confusing",
    "Very Confusing",
    "Not sure",
  ];

  return (
    <Container
      style={{
        position: "relative",
        top: "20%",
        paddingLeft: "15%",
        minHeight: "100vh",
      }}
    >
      {/* Progress Bar – adapt as needed */}
      <div className="progress-container">
        <div style={{ width: "100%", textAlign: "center", marginBottom: "10px" }}>
          <ProgressBar>
            <ProgressBar now={20} label="1. Introduction" key={1} variant="secondary" />
            <ProgressBar now={20} label="2. Participant Info & Consent" key={2} variant="secondary" />
            <ProgressBar now={20} label="3. Pre-study Survey" key={3} variant="secondary" />
            <ProgressBar now={20} label="4. Main Study" key={4} variant="secondary" />
            <ProgressBar now={20} label="5. Post-study Survey" key={5} variant="primary" />
          </ProgressBar>
        </div>
      </div>

      <div className="userQ-popup-container">
        <div className="userQ-popup-content">
          <h1 className="form-title" style={{ textAlign: "center", color: "#093a73", fontWeight: 600, fontSize: 36 }}>
            Post-Study Questionnaire
          </h1>
          <p style={{ textAlign: "center", fontSize: 18, fontWeight: 500, color: "red" }}>** Completing this questionnaire marks the end of the study. **<br></br> <p style = {{color: "grey", fontSize: 16, fontWeight: 400}}>Please ensure you have completed the main study before proceeding. If you wish to return, you can click on the study logo above.</p></p>
          
          <br />
          <br />

          <form>
            {/* --------------------------------------- */}
            {/* Q1.  Impact of Movement & Overlap Info   */}
            {/* --------------------------------------- */}
            <h5 style={{ color: "#093a73" }}>
              ▍Section A: Patient Movement & Ward Overlap Information
            </h5>

            <Form.Group>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>
                  1. How did the addition of <b><u>patient movement and ward overlap information</u></b> impact your decision-making process?
                </span>
              </Form.Label>
              {[
                "Very helpful (e.g. made it easier/made me more confident in my decision)",
                "Somewhat helpful",
                "Neutral (no impact)",
                "Somewhat unhelpful",
                "Very unhelpful (e.g. made it more confusing/gave conflicting information)",
                "Not sure",
              ].map((option) => (
                <Form.Check
                  type="radio"
                  name="Q1_overlap_impact"
                  key={`Q1_overlap_impact-${option}`}
                  id={`Q1_overlap_impact-${option}`}
                  value={option}
                  label={option}
                  onChange={handleChange}
                  checked={formData.Q1_overlap_impact === option}
                  style={{ marginBottom: "8px" }}
                />
              ))}
            </Form.Group>


            {/* ------------------------------------------------ */}
            {/* Q2. Agreement scale: 2a, 2b in a table           */}
            {/* ------------------------------------------------ */}
            <Form.Group style={{ marginTop: "30px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>2. Indicate how strongly you agree or disagree with the statements below:</span>
              </Form.Label>
              <Table bordered size="sm" className="survey-table mt-2" style={{ "--option-count": agreementScale.length }}>
                <thead>
                  <tr>
                    <th></th>
                    {agreementScale.map((option) => (
                      <th key={option}>{option}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      2a. The addition of <b><u>patient movement and ward overlap information</u></b> identified more patients than I initially thought could be potential sources.
                    </td>
                    {agreementScale.map((option) => (
                      <td
                        key={option}
                        onClick={() => handleLikertChange("Q2a_overlap_more_sources", option)}
                        style={{ cursor: "pointer" }}
                      >
                        <label style={{ width: "100%", display: "block" }}>
                          <Form.Check
                            type="radio"
                            name="Q2a_overlap_more_sources"
                            id={`Q2a_overlap_more_sources-${option}`}
                            value={option}
                            onChange={handleChange}
                            checked={formData.Q2a_overlap_more_sources === option}
                            style={{ margin: "0 auto", pointerEvents: "none" }}
                          />
                        </label>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td>
                      2b. The addition of <b><u>patient movement and ward overlap information</u></b> helped narrow down the potential number of sources.
                    </td>
                    {agreementScale.map((option) => (
                      <td
                        key={option}
                        onClick={() => handleLikertChange("Q2b_overlap_helped_narrow", option)}
                        style={{ cursor: "pointer" }}
                      >
                        <label style={{ width: "100%", display: "block" }}>
                          <Form.Check
                            type="radio"
                            name="Q2b_overlap_helped_narrow"
                            id={`Q2b_overlap_helped_narrow-${option}`}
                            value={option}
                            onChange={handleChange}
                            checked={formData.Q2b_overlap_helped_narrow === option}
                            style={{ margin: "0 auto", pointerEvents: "none" }}
                          />
                        </label>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </Table>
            </Form.Group>

            {/* ----------------------------------------------------- */}
            {/* Q3. Uncertainty scale: 3a-f in a table                */}
            {/* ----------------------------------------------------- */}
            <Form.Group style={{ marginTop: "30px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>3. Indicate how the addition of <b><u>patient movement and ward overlap information</u></b> affected your <b><u>UNCERTAINTY</u></b> regarding the scenarios below:</span>
              </Form.Label>
              <Table bordered size="sm" className="survey-table mt-2" style={{ "--option-count": uncertaintyScale.length }}>
                <thead>
                  <tr>
                    <th style={{ width: "40%" }}></th>
                    {uncertaintyScale.map((option) => (
                      <th key={option} style={{ textAlign: "center" }}>
                        {option}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      key: "Q3a_overlap_uncertainty_direct_same_ward",
                      text: "3a. Direct patient-to-patient transmission within the same ward",
                    },
                    {
                      key: "Q3b_overlap_uncertainty_direct_diff_ward_same_spec",
                      text: "3b. Direct patient-to-patient transmission across different wards within the same specialty",
                    },
                    {
                      key: "Q3c_overlap_uncertainty_direct_diff_ward_diff_spec",
                      text: "3c. Direct patient-to-patient transmission across different wards and across specialties",
                    },
                    {
                      key: "Q3d_overlap_uncertainty_environment",
                      text: "3d. Transmission due to environmental/ward contamination",
                    },
                    {
                      key: "Q3e_overlap_uncertainty_hc_team",
                      text: "3e. Transmission from members of the healthcare team to patients",
                    },
                    {
                      key: "Q3f_overlap_uncertainty_other",
                      text: "3f. Other transmissions not covered by the above",
                    },
                  ].map((row) => (
                    <tr key={row.key}>
                      <td>
                        {row.text}
                      </td>
                      {uncertaintyScale.map((option) => (
                        <td
                          key={option}
                          onClick={() => handleLikertChange(row.key, option)}
                          style={{ textAlign: "center", cursor: "pointer" }}
                        >
                          <label style={{ width: "100%", display: "block" }}>
                            <Form.Check
                              type="radio"
                              name={row.key}
                              id={`${row.key}-${option}`}
                              value={option}
                              onChange={handleChange}
                              checked={formData[row.key] === option}
                              style={{ margin: "0 auto", pointerEvents: "none" }}
                            />
                          </label>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Form.Group>

            {/* Q4 optional free text */}
            <Form.Group style={{ marginTop: "30px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>4. Please let us know any other comment on the use or any expected or unexpected findings relating to the addition of <b><u>patient movement and ward overlap information</u></b>.</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="Q4_overlap_comment"
                value={formData.Q4_overlap_comment || ""}
                onChange={handleChange}
                placeholder="Your comments..."
              />
            </Form.Group>

            {/* Q5: Pathogen genetic info (helpful scale) */}
            <h5 style={{ marginTop: "50px", color: "#093a73" }}>
              ▍Section B: Pathogen Genetic Information
            </h5>

            <Form.Group>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>
                  5. How did the addition of <b><u>pathogen genetic information</u></b> impact your decision-making process?
                </span>
              </Form.Label>
              {[
                "Very helpful (e.g. made it easier/made me more confident in my decision)",
                "Somewhat helpful",
                "Neutral (no impact)",
                "Somewhat unhelpful",
                "Very unhelpful (e.g. made it more confusing/gave conflicting information)",
                "Not sure",
              ].map((option) => (
                <Form.Check
                  type="radio"
                  name="Q5_genetic_impact"
                  key={`Q5_genetic_impact-${option}`}
                  id={`Q5_genetic_impact-${option}`}
                  value={option}
                  label={option}
                  onChange={handleChange}
                  checked={formData.Q5_genetic_impact === option}
                  style={{ marginBottom: "8px" }}
                />
              ))}
            </Form.Group>

            {/* Q6: Agreement scale, 6a & 6b */}
            <Form.Group style={{ marginTop: "30px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>6. Indicate how strongly you agree or disagree with the statements below:</span>
              </Form.Label>
              <Table bordered size="sm" className="survey-table mt-2" style={{ "--option-count": agreementScale.length }}>
                <thead>
                  <tr>
                    <th style={{ width: "40%" }}></th>
                    {agreementScale.map((option) => (
                      <th key={option} style={{ textAlign: "center" }}>
                        {option}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      6a. The addition of <b><u>pathogen genetic information</u></b> identified more patients than I initially thought could be potential sources.
                    </td>
                    {agreementScale.map((option) => (
                      <td
                        key={option}
                        onClick={() => handleLikertChange("Q6a_genetic_more_sources", option)}
                        style={{ textAlign: "center", cursor: "pointer" }}
                      >
                        <label style={{ width: "100%", display: "block" }}>
                          <Form.Check
                            type="radio"
                            name="Q6a_genetic_more_sources"
                            id={`Q6a_genetic_more_sources-${option}`}
                            value={option}
                            onChange={handleChange}
                            checked={formData.Q6a_genetic_more_sources === option}
                            style={{ margin: "0 auto", pointerEvents: "none" }}
                          />
                        </label>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td>
                      6b. The addition of <b><u>pathogen genetic information</u></b> helped narrow down the potential number of sources.
                    </td>
                    {agreementScale.map((option) => (
                      <td
                        key={option}
                        onClick={() => handleLikertChange("Q6b_genetic_helped_narrow", option)}
                        style={{ textAlign: "center", cursor: "pointer" }}
                      >
                        <label style={{ width: "100%", display: "block" }}>
                          <Form.Check
                            type="radio"
                            name="Q6b_genetic_helped_narrow"
                            id={`Q6b_genetic_helped_narrow-${option}`}
                            value={option}
                            onChange={handleChange}
                            checked={formData.Q6b_genetic_helped_narrow === option}
                            style={{ margin: "0 auto", pointerEvents: "none" }}
                          />
                        </label>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </Table>
            </Form.Group>

            {/* Q7: Uncertainty scale, 7a-f */}
            <Form.Group style={{ marginTop: "30px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>7. Indicate how the addition of <b><u>pathogen genetic information</u></b> affected your <b><u>UNCERTAINTY</u></b> regarding the scenarios below:</span>
              </Form.Label>
              <Table bordered size="sm" className="survey-table mt-2" style={{ "--option-count": uncertaintyScale.length }}>
                <thead>
                  <tr>
                    <th style={{ width: "40%" }}></th>
                    {uncertaintyScale.map((option) => (
                      <th key={option} style={{ textAlign: "center" }}>
                        {option}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      key: "Q7a_genetic_uncertainty_direct_same_ward",
                      text: "7a. Direct patient-to-patient transmission within the same ward",
                    },
                    {
                      key: "Q7b_genetic_uncertainty_direct_diff_ward_same_spec",
                      text: "7b. Direct patient-to-patient transmission across different wards within the same specialty",
                    },
                    {
                      key: "Q7c_genetic_uncertainty_direct_diff_ward_diff_spec",
                      text: "7c. Direct patient-to-patient transmission across different wards & across specialties",
                    },
                    {
                      key: "Q7d_genetic_uncertainty_environment",
                      text: "7d. Transmission due to environmental/ward contamination",
                    },
                    {
                      key: "Q7e_genetic_uncertainty_hc_team",
                      text: "7e. Transmission from members of the healthcare team to patients",
                    },
                    {
                      key: "Q7f_genetic_uncertainty_other",
                      text: "7f. Other transmissions not covered by the above",
                    },
                  ].map((row) => (
                    <tr key={row.key}>
                      <td>
                        {row.text}
                      </td>
                      {uncertaintyScale.map((option) => (
                        <td
                          key={option}
                          onClick={() => handleLikertChange(row.key, option)}
                          style={{ textAlign: "center", cursor: "pointer" }}
                        >
                          <label style={{ width: "100%", display: "block" }}>
                            <Form.Check
                              type="radio"
                              name={row.key}
                              id={`${row.key}-${option}`}
                              value={option}
                              onChange={handleChange}
                              checked={formData[row.key] === option}
                              style={{ margin: "0 auto", pointerEvents: "none" }}
                            />
                          </label>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Form.Group>

            {/* Q8 optional free text */}
            <Form.Group style={{ marginTop: "30px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>8. Please let us know any other comment on the use or any expected or unexpected findings relating to the addition of <b><u>pathogen genetic information</u></b>.</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="Q8_genetic_comment"
                value={formData.Q8_genetic_comment || ""}
                onChange={handleChange}
                placeholder="Your comments..."
              />
            </Form.Group>

            {/* Q9: Specific features that influenced decisions */}
            <Form.Group style={{ marginTop: "30px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>9. Were there specific features or pieces of information on the dashboard that particularly influenced your decisions?</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="Q9_dashboard_info_comment"
                value={formData.Q9_dashboard_info_comment || ""}
                onChange={handleChange}
                placeholder="Your comments..."
              />
            </Form.Group>

            <br />

            {/* --------------------- */}
            {/* SECTION B: Experience */}
            {/* --------------------- */}

            <h5 style={{ marginTop: "50px", color: "#093a73" }}>
              ▍Section C: Dashboard Experience
            </h5>

            {/* 10a: Overall Experience, 1..10 */}
            <Form.Group>
              <Form.Label>
              <span style={{ fontWeight: 500 }}>10a. How would you rate your overall experience with the IPC Dashboard?</span>
              </Form.Label>
              <div className="likert-scale" style={{ marginTop: "5px" }}>
                {[...Array(10).keys()].map((num) => (
                  <Button
                    key={num + 1}
                    variant={formData.Q10a_dashboard_overall_experience_rating === (num + 1).toString() ? "primary" : "outline-secondary"}
                    onClick={() => handleLikertChange("Q10a_dashboard_overall_experience_rating", (num + 1).toString())}
                    className="likert-button"
                  >
                    {num + 1}
                  </Button>
                ))}
              </div>
              <div style={{ color: "grey", fontSize: "0.9em" }}>
                (1 = Extremely negative)&emsp;&emsp;&emsp;&emsp;&emsp;&emsp;&emsp;&emsp;&nbsp;&ensp;(10 = Extremely positive)
              </div>
            </Form.Group>

            {/* 10b: free text */}
            <Form.Group style={{ marginTop: "20px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>10b. Please let us know if you have any comments on your overall experience with the IPC Dashboard.</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="Q10b_dashboard_overall_experience_comment"
                value={formData.Q10b_dashboard_overall_experience_comment || ""}
                onChange={handleChange}
                placeholder="Your comments..."
              />
            </Form.Group>

            {/* 11a: User-Friendliness, 1..10 */}
            <Form.Group style={{ marginTop: "30px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>11a. How user friendly did you find the dashboard interface?</span>
              </Form.Label>
              <div className="likert-scale" style={{ marginTop: "5px" }}>
                {[...Array(10).keys()].map((num) => (
                  <Button
                    key={num + 1}
                    variant={formData.Q11a_dashboard_user_friendliness_rating === (num + 1).toString() ? "primary" : "outline-secondary"}
                    onClick={() => handleLikertChange("Q11a_dashboard_user_friendliness_rating", (num + 1).toString())}
                    className="likert-button"
                  >
                    {num + 1}
                  </Button>
                ))}
              </div>
              <div style={{ color: "grey", fontSize: "0.9em" }}>
                (1 = Very difficult to navigate)&emsp;&emsp;&emsp;&emsp;&emsp;&emsp;&ensp;(10 = Very user-friendly)
              </div>
            </Form.Group>

            {/* 11b: free text */}
            <Form.Group style={{ marginTop: "20px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>11b. Please let us know if you have any comments on the user friendliness of the IPC Dashboard.</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="Q11b_dashboard_user_friendliness_comment"
                value={formData.Q11b_dashboard_user_friendliness_comment || ""}
                onChange={handleChange}
                placeholder="Your comments..."
              />
            </Form.Group>

            {/* Q12: Presentation & Clarity scale (5 options) */}
            <Form.Group style={{ marginTop: "30px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>12. Presentation and Clarity</span>
              </Form.Label>
              <Table bordered size="sm" className="survey-table mt-2" style={{ "--option-count": clarityScale.length }}>
                <thead>
                  <tr>
                    <th style={{ width: "40%" }}></th>
                    {clarityScale.map((option) => (
                      <th key={option} style={{ textAlign: "center" }}>
                        {option}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      key: "Q12a_dashboard_clarity_case_list",
                      text: "12a. Were the case list and summary information displayed on the dashboard clear and easily comprehensible?",
                      highlight: "case list and summary information",
                    },
                    {
                      key: "Q12b_dashboard_clarity_overlap",
                      text: "12b. Were the patient movement/ward overlap information displayed on the dashboard clear and easily comprehensible?",
                      highlight: "patient movement/ward overlap information",
                    },
                    {
                      key: "Q12c_dashboard_clarity_genetic",
                      text: "12c. Were the pathogen genetic information displayed on the dashboard clear and easily comprehensible?",
                      highlight: "pathogen genetic information",
                    },
                  ].map((row) => {
                    const highlightedText = row.text.replace(
                      row.highlight,
                      `<b><u>${row.highlight}</u></b>`
                    );
                    return (
                      <tr key={row.key}>
                        <td dangerouslySetInnerHTML={{ __html: highlightedText }} />
                        {clarityScale.map((option) => (
                          <td
                            key={option}
                            onClick={() => handleLikertChange(row.key, option)}
                            style={{ textAlign: "center", cursor: "pointer" }}
                          >
                            <label style={{ width: "100%", display: "block" }}>
                              <Form.Check
                                type="radio"
                                name={row.key}
                                id={`${row.key}-${option}`}
                                value={option}
                                onChange={handleChange}
                                checked={formData[row.key] === option}
                                style={{ margin: "0 auto", pointerEvents: "none" }}
                              />
                            </label>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Form.Group>

            {/* 12d: optional free text */}
            <Form.Group style={{ marginTop: "20px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>12d. Please let us know if you have any comments on the presentation and clarity of the IPC Dashboard.</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="Q12d_dashboard_clarity_comment"
                value={formData.Q12d_dashboard_clarity_comment || ""}
                onChange={handleChange}
                placeholder="Your comments..."
              />
            </Form.Group>

            {/* 13: Challenges */}
            <Form.Group style={{ marginTop: "30px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>13. Did you encounter any challenges or difficulties while using the dashboard or completing the case scenarios?</span>
              </Form.Label>
              {["Yes", "No"].map((label) => (
                <Form.Check
                  name="Q13_dashboard_challenges"
                  type="radio"
                  id={`Q13_dashboard_challenges-${label}`}
                  key={`Q13_dashboard_challenges-${label}`}
                  label={label}
                  onChange={handleChange}
                  value={label}
                  checked={formData.Q13_dashboard_challenges === label}
                />
              ))}
              {formData.Q13_dashboard_challenges === "Yes" && (
                <div style={{ marginTop: "10px" }}>
                  <Form.Label>Please specify:</Form.Label>
                  <Form.Control
                    type="text"
                    name="Q13_dashboard_challenges_comment"
                    value={formData.Q13_dashboard_challenges_comment || ""}
                    onChange={handleChange}
                    placeholder="Describe the challenges you faced."
                  />
                </div>
              )}
            </Form.Group>

            {/* 14. Recommendations (Optional) */}
            <Form.Group style={{ marginTop: "30px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>14. Please let us know if you have any recommendations or features you think would enhance the dashboard's utility and user experience.</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="Q14_dashboard_recommendations"
                value={formData.Q14_dashboard_recommendations || ""}
                onChange={handleChange}
                placeholder="Your suggestions..."
              />
            </Form.Group>

            {/* 15. Additional feedback (Optional) */}
            <Form.Group style={{ marginTop: "30px" }}>
              <Form.Label>
                <span style={{ fontWeight: 500 }}>15. Please let us have any additional feedback, comments, or insights you might have about the study or the dashboard.</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="Q15_dashboard_additional_feedback"
                value={formData.Q15_dashboard_additional_feedback || ""}
                onChange={handleChange}
                placeholder="Any further feedback..."
              />
            </Form.Group>

            <br />
            <Button className="blue-button-form" onClick={handleSubmit}>
              Submit &amp; Finish
            </Button>
          </form>
        </div>
      </div>

      {/* Modal for incomplete form */}
      <Modal className="modal-lg" show={showConfirmation} onHide={() => setShowConfirmation(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Incomplete Questionnaire</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Some responses are missing:</p>
          <ul>
            {missingFields.map((field, index) => (
              <li key={index}>
                <strong>{field}</strong>
              </li>
            ))}
          </ul>
          <p>Would you still like to submit the questionnaire?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmation(false)}>
            No, go back
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowConfirmation(false);
              submitForm();
            }}
          >
            Yes, submit anyway
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
